import React, { useState, useEffect, useRef, useCallback } from 'react';
import 'quill/dist/quill.snow.css';
import { Mail, Save, TestTube2, Loader2, Plus, Pencil, Trash2, Eye, EyeOff, CheckCircle, XCircle, Code } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const getH = (json = true) => ({
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
  ...(json ? { 'Content-Type': 'application/json' } : {}),
});

// ── Native Quill v2 WYSIWYG wrapper (React 18 safe) ──────────────────────────
const QuillEditor = ({ value, onChange, placeholder, onQuillReady }) => {
  const containerRef = useRef(null);
  const quillRef = useRef(null);
  const initializedRef = useRef(false);   // persists across StrictMode cycles
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    // Skip StrictMode's second invocation — ref persists, DOM is fresh new mount
    if (initializedRef.current) return;
    if (!containerRef.current) return;
    initializedRef.current = true;

    const Quill = require('quill').default;
    const quill = new Quill(containerRef.current, {
      theme: 'snow',
      placeholder: placeholder || 'Write your email body here...',
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link'],
          ['clean'],
        ],
      },
    });
    quillRef.current = quill;
    if (onQuillReady) onQuillReady(quill);

    if (value) quill.clipboard.dangerouslyPasteHTML(value);

    quill.on('text-change', () => {
      onChangeRef.current(quill.root.innerHTML);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync external value changes
  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;
    const current = quill.root.innerHTML;
    if (value !== current) quill.clipboard.dangerouslyPasteHTML(value || '');
  }, [value]);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <style>{`
        .ql-toolbar.ql-snow { border-bottom: 1px solid #e5e7eb !important; background: #f9fafb; border-top: none; border-left: none; border-right: none; }
        .ql-container.ql-snow { border: none !important; font-family: Arial, sans-serif; font-size: 14px; min-height: 220px; }
        .ql-editor { min-height: 220px; }
      `}</style>
      <div ref={containerRef} />
    </div>
  );
};

// ── Variable chip with click-to-insert, copy, and drag support ──────────────
const VariableChip = ({ varName, onInsert }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(`{{${varName}}}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const handleDragStart = (e) => {
    e.dataTransfer.setData('text/plain', `{{${varName}}}`);
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <span
      draggable
      onDragStart={handleDragStart}
      onClick={() => onInsert(varName)}
      title="Click to insert at cursor  •  Drag into editor  •  Use copy button"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: 11, fontFamily: 'monospace',
        background: '#eff6ff', color: '#1d4ed8',
        border: '1px solid #bfdbfe', borderRadius: 5,
        padding: '3px 4px 3px 8px', margin: '2px 3px',
        cursor: 'grab', userSelect: 'none',
        transition: 'background 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = '#dbeafe'}
      onMouseLeave={e => e.currentTarget.style.background = '#eff6ff'}
    >
      {`{{${varName}}}`}
      <button
        onClick={handleCopy}
        title="Copy to clipboard"
        style={{
          background: copied ? '#bbf7d0' : '#e0e7ff',
          border: 'none', borderRadius: 3,
          padding: '1px 5px', cursor: 'pointer',
          fontSize: 10, color: copied ? '#15803d' : '#3730a3',
          fontFamily: 'sans-serif', fontWeight: 600,
          transition: 'background 0.15s',
        }}
      >
        {copied ? '✓' : 'copy'}
      </button>
    </span>
  );
};

const TEMPLATE_PRESETS = [
  {
    key: 'can_issued',
    label: 'Cargo Arrival Notice (CAN)',
    defaultName: 'Cargo Arrival Notice',
    defaultSubject: 'Cargo Arrival Notice — {{docket_no}}',
    variables: ['docket_no', 'consignee_name', 'shipper_name', 'description', 'packages', 'weight'],
    defaultBody: `<div style="font-family:Arial,sans-serif;max-width:620px;color:#1f2937">
  <p>Dear <strong>{{consignee_name}}</strong>,</p>
  <p>We are pleased to inform you that your cargo has arrived and is now available for collection.</p>
  <table style="width:100%;border-collapse:collapse;margin:16px 0;font-size:14px">
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600;width:40%">Docket No.</td><td style="padding:8px 12px;border:1px solid #e5e7eb"><strong>{{docket_no}}</strong></td></tr>
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600">Shipper</td><td style="padding:8px 12px;border:1px solid #e5e7eb">{{shipper_name}}</td></tr>
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600">Description</td><td style="padding:8px 12px;border:1px solid #e5e7eb">{{description}}</td></tr>
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600">No. of Packages</td><td style="padding:8px 12px;border:1px solid #e5e7eb">{{packages}}</td></tr>
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600">Weight</td><td style="padding:8px 12px;border:1px solid #e5e7eb">{{weight}} kg</td></tr>
  </table>
  <p>Please bring this notice along with your identification when collecting the cargo. Kindly contact us to schedule a convenient pickup time.</p>
  <p>For any queries, feel free to reach out to us.</p>
  <p>Warm regards,<br/><strong>Stavan Logistics Team</strong></p>
</div>`,
  },
  {
    key: 'delivery_order',
    label: 'Delivery Order (DO)',
    defaultName: 'Delivery Order',
    defaultSubject: 'Delivery Order — {{docket_no}}',
    variables: ['docket_no', 'consignee_name', 'do_no', 'description'],
    defaultBody: `<div style="font-family:Arial,sans-serif;max-width:620px;color:#1f2937">
  <p>Dear <strong>{{consignee_name}}</strong>,</p>
  <p>Please find attached the Delivery Order for your shipment. Kindly present this document at the time of cargo collection.</p>
  <table style="width:100%;border-collapse:collapse;margin:16px 0;font-size:14px">
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600;width:40%">Docket No.</td><td style="padding:8px 12px;border:1px solid #e5e7eb"><strong>{{docket_no}}</strong></td></tr>
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600">DO No.</td><td style="padding:8px 12px;border:1px solid #e5e7eb">{{do_no}}</td></tr>
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600">Description of Goods</td><td style="padding:8px 12px;border:1px solid #e5e7eb">{{description}}</td></tr>
  </table>
  <p>Please ensure all necessary customs and port clearances are completed before presenting this order.</p>
  <p>For assistance, do not hesitate to contact our operations team.</p>
  <p>Regards,<br/><strong>Stavan Logistics Team</strong></p>
</div>`,
  },
  {
    key: 'air_waybill',
    label: 'Air Waybill (AWB)',
    defaultName: 'Air Waybill',
    defaultSubject: 'Air Waybill — {{docket_no}}',
    variables: ['docket_no', 'awb_no', 'shipper_name', 'consignee_name', 'airport_departure', 'airport_destination'],
    defaultBody: `<div style="font-family:Arial,sans-serif;max-width:620px;color:#1f2937">
  <p>Dear <strong>{{consignee_name}}</strong>,</p>
  <p>Please find attached the Air Waybill for your shipment. The details are as follows:</p>
  <table style="width:100%;border-collapse:collapse;margin:16px 0;font-size:14px">
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600;width:40%">AWB No.</td><td style="padding:8px 12px;border:1px solid #e5e7eb"><strong>{{awb_no}}</strong></td></tr>
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600">Docket No.</td><td style="padding:8px 12px;border:1px solid #e5e7eb">{{docket_no}}</td></tr>
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600">Shipper</td><td style="padding:8px 12px;border:1px solid #e5e7eb">{{shipper_name}}</td></tr>
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600">Airport of Departure</td><td style="padding:8px 12px;border:1px solid #e5e7eb">{{airport_departure}}</td></tr>
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600">Airport of Destination</td><td style="padding:8px 12px;border:1px solid #e5e7eb">{{airport_destination}}</td></tr>
  </table>
  <p>Please retain this document for your records. The attached PDF contains the full Air Waybill with all shipping details.</p>
  <p>Regards,<br/><strong>Stavan Logistics Team</strong></p>
</div>`,
  },
  {
    key: 'freight_certificate',
    label: 'Freight Certificate (FC)',
    defaultName: 'Freight Certificate',
    defaultSubject: 'Freight Certificate — {{docket_no}}',
    variables: ['docket_no', 'consignee_name', 'description'],
    defaultBody: `<div style="font-family:Arial,sans-serif;max-width:620px;color:#1f2937">
  <p>Dear <strong>{{consignee_name}}</strong>,</p>
  <p>We are sharing your Freight Certificate for docket <strong>{{docket_no}}</strong>. Please find the document attached for your records.</p>
  <table style="width:100%;border-collapse:collapse;margin:16px 0;font-size:14px">
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600;width:40%">Docket No.</td><td style="padding:8px 12px;border:1px solid #e5e7eb"><strong>{{docket_no}}</strong></td></tr>
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600">Description of Goods</td><td style="padding:8px 12px;border:1px solid #e5e7eb">{{description}}</td></tr>
  </table>
  <p>This certificate confirms the freight charges applicable for the above shipment. Please review and contact us for any discrepancies.</p>
  <p>Thank you for your business.</p>
  <p>Regards,<br/><strong>Stavan Logistics Team</strong></p>
</div>`,
  },
  {
    key: 'docket_created',
    label: 'Docket / LR Receipt',
    defaultName: 'Docket Confirmation',
    defaultSubject: 'Shipment Confirmation — {{docket_no}}',
    variables: ['docket_no', 'customer_name', 'from_location', 'to_location', 'packages', 'weight'],
    defaultBody: `<div style="font-family:Arial,sans-serif;max-width:620px;color:#1f2937">
  <p>Dear <strong>{{customer_name}}</strong>,</p>
  <p>Your shipment has been successfully registered. Please find the LR Receipt / Docket attached for your reference.</p>
  <table style="width:100%;border-collapse:collapse;margin:16px 0;font-size:14px">
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600;width:40%">Docket No.</td><td style="padding:8px 12px;border:1px solid #e5e7eb"><strong>{{docket_no}}</strong></td></tr>
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600">Origin</td><td style="padding:8px 12px;border:1px solid #e5e7eb">{{from_location}}</td></tr>
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600">Destination</td><td style="padding:8px 12px;border:1px solid #e5e7eb">{{to_location}}</td></tr>
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600">No. of Packages</td><td style="padding:8px 12px;border:1px solid #e5e7eb">{{packages}}</td></tr>
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600">Total Weight</td><td style="padding:8px 12px;border:1px solid #e5e7eb">{{weight}} kg</td></tr>
  </table>
  <p>You can track your shipment using the docket number above. Please contact us for any queries.</p>
  <p>Thank you for choosing Stavan Logistics.</p>
  <p>Warm regards,<br/><strong>Stavan Logistics Team</strong></p>
</div>`,
  },
  {
    key: 'manifest',
    label: 'Manifest',
    defaultName: 'Manifest',
    defaultSubject: 'Manifest — {{manifest_no}}',
    variables: ['docket_no', 'manifest_no', 'vehicle_no', 'driver_name', 'from_location', 'to_location'],
    defaultBody: `<div style="font-family:Arial,sans-serif;max-width:620px;color:#1f2937">
  <p>Dear Team,</p>
  <p>Please find attached the Manifest for the following dispatch:</p>
  <table style="width:100%;border-collapse:collapse;margin:16px 0;font-size:14px">
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600;width:40%">Manifest No.</td><td style="padding:8px 12px;border:1px solid #e5e7eb"><strong>{{manifest_no}}</strong></td></tr>
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600">Docket No.</td><td style="padding:8px 12px;border:1px solid #e5e7eb">{{docket_no}}</td></tr>
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600">Vehicle No.</td><td style="padding:8px 12px;border:1px solid #e5e7eb">{{vehicle_no}}</td></tr>
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600">Driver</td><td style="padding:8px 12px;border:1px solid #e5e7eb">{{driver_name}}</td></tr>
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600">From</td><td style="padding:8px 12px;border:1px solid #e5e7eb">{{from_location}}</td></tr>
    <tr><td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:600">To</td><td style="padding:8px 12px;border:1px solid #e5e7eb">{{to_location}}</td></tr>
  </table>
  <p>Please acknowledge receipt and confirm dispatch at the earliest.</p>
  <p>Regards,<br/><strong>Stavan Logistics — Operations</strong></p>
</div>`,
  },
  {
    key: 'custom',
    label: '+ Custom Template Key…',
    defaultName: '',
    defaultSubject: '',
    defaultBody: '',
    variables: [],
  },
];

// Build lookup from TEMPLATE_PRESETS for variable chips fallback
const TEMPLATE_VARIABLE_PRESETS = Object.fromEntries(
  TEMPLATE_PRESETS.filter(p => p.key !== 'custom').map(p => [p.key, p.variables])
);

// ── Template editor modal ─────────────────────────────────────────────────────
const TemplateModal = ({ template, onClose, onSave }) => {
  const [form, setForm] = useState(() => {
    if (!template) return { template_key: '', name: '', subject: '', html_body: '', variables: [], _selectedPreset: '' };
    const known = TEMPLATE_PRESETS.find(p => p.key === template.template_key);
    return { ...template, _selectedPreset: known ? template.template_key : 'custom' };
  });
  const [showHtml, setShowHtml] = useState(false);
  const [saving, setSaving] = useState(false);
  const quillInstanceRef = useRef(null);
  const { toast } = useToast();

  const variableList = (() => {
    const v = form.variables;
    const fromField = Array.isArray(v) ? v : (typeof v === 'string' ? v.split(',').map(x => x.trim()).filter(Boolean) : []);
    // If field is empty, fall back to preset for the selected template key
    return fromField.length > 0 ? fromField : (TEMPLATE_VARIABLE_PRESETS[form.template_key] || []);
  })();

  // Insert {{variable}} at current cursor via Quill imperative ref
  const insertVariable = (varName) => {
    const quill = quillInstanceRef.current;
    if (!quill) return;
    const range = quill.getSelection(true);
    const idx = range ? range.index : quill.getLength();
    quill.insertText(idx, `{{${varName}}}`);
    quill.setSelection(idx + varName.length + 4);
  };

  const save = async () => {
    if (!form.template_key || !form.name || !form.subject) {
      toast({ title: 'Please fill in Template Type, Name and Subject', variant: 'destructive' }); return;
    }
    setSaving(true);
    try {
      const isNew = !form.id;
      const res = await fetch(
        isNew ? `${backendUrl}/api/email-templates` : `${backendUrl}/api/email-templates/${form.id}`,
        {
          method: isNew ? 'POST' : 'PUT',
          headers: getH(),
          body: JSON.stringify({
            ...form,
            variables: typeof form.variables === 'string'
              ? form.variables.split(',').map(v => v.trim()).filter(Boolean)
              : form.variables
          }),
        }
      );
      if (!res.ok) {
        const e = await res.json();
        const msg = e.detail === 'Template key already exists'
          ? 'A template for this document type already exists. Edit the existing one instead of creating a new one.'
          : (e.detail || 'Save failed');
        throw new Error(msg);
      }
      onSave();
      onClose();
      toast({ title: 'Template saved' });
    } catch (e) { toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800 font-manrope">{form.id ? 'Edit Template' : 'New Template'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">

          {/* Template Key dropdown */}
          <div>
            <label className="text-xs font-semibold text-gray-500 font-ibm mb-1 block">Template Type *</label>
            {form.id ? (
              /* Editing — show key as read-only badge */
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-3 py-1.5 rounded-md bg-gray-100 text-gray-700 text-sm font-mono border border-gray-200">
                  {form.template_key}
                </span>
                <span className="text-xs text-gray-400 font-ibm">Template key cannot be changed after creation</span>
              </div>
            ) : (
              <div className="relative">
                <select
                  data-testid="tmpl-key"
                  value={form._selectedPreset || ''}
                  onChange={e => {
                    const chosen = e.target.value;
                    if (chosen === 'custom') {
                      setForm(p => ({ ...p, _selectedPreset: 'custom', template_key: '', name: '', subject: '', variables: [] }));
                      return;
                    }
                    const preset = TEMPLATE_PRESETS.find(p => p.key === chosen);
                    if (!preset) return;
                    setForm(p => ({
                      ...p,
                      _selectedPreset: chosen,
                      template_key: preset.key,
                      name: p.name || preset.defaultName,
                      subject: p.subject || preset.defaultSubject,
                      variables: p.variables?.length ? p.variables : preset.variables,
                      html_body: p.html_body || preset.defaultBody,
                    }));
                  }}
                  className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 bg-white text-gray-800 appearance-none pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">— Select document type —</option>
                  {TEMPLATE_PRESETS.map(p => (
                    <option key={p.key} value={p.key}>{p.label}</option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▼</span>
              </div>
            )}
          </div>

          {/* Custom key input — only shown when "custom" is selected */}
          {(!form.id && form._selectedPreset === 'custom') && (
            <div>
              <label className="text-xs font-semibold text-gray-500 font-ibm mb-1 block">
                Custom Template Key * <span className="font-normal text-gray-400">(lowercase, underscores only)</span>
              </label>
              <Input value={form.template_key}
                onChange={e => setForm(p => ({ ...p, template_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))}
                placeholder="e.g. invoice_sent" className="text-sm font-mono" data-testid="tmpl-key-custom" />
            </div>
          )}

          {/* Name + Subject — shown after type is picked */}
          {(form.id || form._selectedPreset) && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 font-ibm mb-1 block">Display Name *</label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Cargo Arrival Notice" className="text-sm" data-testid="tmpl-name" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 font-ibm mb-1 block">Email Subject *</label>
                <Input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                  placeholder="e.g. CAN — {{docket_no}}" className="text-sm" data-testid="tmpl-subject" />
              </div>
            </div>
          )}

          {/* Variables — show only after type is picked */}
          {(form.id || form._selectedPreset) && (
            <>
              <div>
                <label className="text-xs font-semibold text-gray-500 font-ibm mb-1 block">
                  Available Variables <span className="font-normal text-gray-400">(comma separated — edit to add custom ones)</span>
                </label>
                <Input
                  value={Array.isArray(form.variables) ? form.variables.join(', ') : (form.variables || '')}
                  onChange={e => setForm(p => ({ ...p, variables: e.target.value }))}
                  placeholder="docket_no, consignee_name, shipper_name"
                  className="text-sm font-mono" data-testid="tmpl-vars" />
              </div>

              {/* Variable chips */}
              {variableList.length > 0 && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-blue-700 font-ibm">Available Variables</span>
                    <span className="text-xs text-blue-400 font-ibm">— click to insert at cursor · drag into editor · or copy</span>
                  </div>
                  <div className="flex flex-wrap">
                    {variableList.map(v => (
                      <VariableChip key={v} varName={v} onInsert={insertVariable} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Body editor — shown only after type is picked */}
          {(form.id || form._selectedPreset) && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-500 font-ibm">Email Body</label>
              <button
                onClick={() => setShowHtml(p => !p)}
                className="flex items-center gap-1 text-xs text-blue-600 hover:underline font-ibm"
              >
                <Code className="w-3 h-3" />
                {showHtml ? 'Visual Editor' : 'HTML Source'}
              </button>
            </div>

            {showHtml ? (
              <Textarea
                value={form.html_body}
                onChange={e => setForm(p => ({ ...p, html_body: e.target.value }))}
                rows={12}
                className="text-xs font-mono"
                placeholder="<p>Paste raw HTML here...</p>"
                data-testid="tmpl-body-html"
              />
            ) : (
              <QuillEditor
                value={form.html_body}
                onChange={val => setForm(p => ({ ...p, html_body: val }))}
                placeholder="Write your email body here. Use the toolbar to format. Click variable chips above to insert dynamic fields."
                onQuillReady={q => { quillInstanceRef.current = q; }}
              />
            )}
          </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2 bg-gray-50 rounded-b-2xl">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={save} disabled={saving} data-testid="save-template-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            Save Template
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── Main Email Settings Page ──────────────────────────────────────────────────
const EmailSettings = () => {
  const { toast } = useToast();

  // SMTP config
  const [smtp, setSmtp] = useState({
    smtp_host: 'smtp.gmail.com', smtp_port: 587,
    smtp_username: 'info@stavan.co.in', smtp_password: '',
    from_name: 'Stavan Logistics', from_email: 'info@stavan.co.in',
    reply_to_email: '', is_enabled: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [smtpStatus, setSmtpStatus] = useState(null); // 'ok' | 'fail' | null

  // Templates
  const [templates, setTemplates] = useState([]);
  const [tmplLoading, setTmplLoading] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null); // null = closed, {} = new, {...} = existing
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchTemplates();
  }, []);

  const fetchSettings = async () => {
    setSmtpLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/email-settings`, { headers: getH() });
      if (!res.ok) return;
      const data = await res.json();
      if (data) setSmtp(data);
    } catch (_) {}
    finally { setSmtpLoading(false); }
  };

  const fetchTemplates = async () => {
    setTmplLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/email-templates`, { headers: getH() });
      if (!res.ok) return;
      const data = await res.json();
      setTemplates(Array.isArray(data) ? data : data.templates || []);
    } catch (_) {}
    finally { setTmplLoading(false); }
  };

  const saveSmtp = async () => {
    setSmtpSaving(true);
    try {
      const res = await fetch(`${backendUrl}/api/email-settings/upsert`, {
        method: 'POST', headers: getH(),
        body: JSON.stringify(smtp),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Save failed'); }
      const saved = await res.json();
      setSmtp(saved);
      toast({ title: 'SMTP settings saved' });
    } catch (e) { toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
    finally { setSmtpSaving(false); }
  };

  const testConnection = async () => {
    setTestLoading(true);
    setSmtpStatus(null);
    try {
      const res = await fetch(`${backendUrl}/api/email-settings/test-smtp`, {
        method: 'POST', headers: getH(),
        body: JSON.stringify({ test_email: testEmail || smtp.smtp_username }),
      });
      if (res.ok) {
        setSmtpStatus('ok');
        toast({ title: 'Connection successful!', description: 'SMTP is working correctly' });
      } else {
        const e = await res.json();
        setSmtpStatus('fail');
        toast({ title: 'Connection failed', description: e.detail, variant: 'destructive' });
      }
    } catch (e) { setSmtpStatus('fail'); toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
    finally { setTestLoading(false); }
  };

  const seedTemplates = async () => {
    setSeeding(true);
    try {
      const res = await fetch(`${backendUrl}/api/email-templates/seed-defaults`, { method: 'POST', headers: getH() });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      const data = await res.json();
      await fetchTemplates();
      toast({ title: 'Templates seeded', description: data.message });
    } catch (e) { toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
    finally { setSeeding(false); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      const res = await fetch(`${backendUrl}/api/email-templates/${id}`, { method: 'DELETE', headers: getH() });
      if (!res.ok) throw new Error('Delete failed');
      setTemplates(p => p.filter(t => t.id !== id));
      toast({ title: 'Template deleted' });
    } catch (e) { toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
  };

  return (
    <div className="space-y-6" data-testid="email-settings-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
          <Mail className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900 font-manrope">Email & SMTP Settings</h2>
          <p className="text-sm text-gray-500 font-ibm">Configure Gmail SMTP for sending transactional emails</p>
        </div>
      </div>

      {/* ── SMTP Configuration ── */}
      <Card className="p-6 rounded-xl border border-gray-200 bg-white">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-semibold text-gray-800 font-manrope">SMTP Configuration</h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <div
              onClick={() => setSmtp(p => ({ ...p, is_enabled: !p.is_enabled }))}
              className={`relative w-10 h-5 rounded-full transition-colors ${smtp.is_enabled ? 'bg-blue-600' : 'bg-gray-200'}`}
              data-testid="smtp-enabled-toggle"
            >
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${smtp.is_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </div>
            <span className="text-sm font-ibm text-gray-700">{smtp.is_enabled ? 'Enabled' : 'Disabled'}</span>
          </label>
        </div>

        {smtpLoading ? (
          <div className="flex items-center gap-2 text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">Loading...</span></div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 font-ibm mb-1 block">SMTP Host</label>
                <Input value={smtp.smtp_host || ''} onChange={e => setSmtp(p => ({ ...p, smtp_host: e.target.value }))}
                  placeholder="smtp.gmail.com" data-testid="smtp-host" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 font-ibm mb-1 block">SMTP Port</label>
                <Input type="number" value={smtp.smtp_port || 587} onChange={e => setSmtp(p => ({ ...p, smtp_port: parseInt(e.target.value) }))}
                  placeholder="587" data-testid="smtp-port" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 font-ibm mb-1 block">Gmail Username (email)</label>
                <Input value={smtp.smtp_username || ''} onChange={e => setSmtp(p => ({ ...p, smtp_username: e.target.value }))}
                  placeholder="info@stavan.co.in" data-testid="smtp-username" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 font-ibm mb-1 block">
                  App Password
                  <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="ml-1 text-blue-600 font-normal text-[10px] hover:underline">
                    Get App Password →
                  </a>
                </label>
                <div className="relative">
                  <Input type={showPassword ? 'text' : 'password'} value={smtp.smtp_password || ''}
                    onChange={e => setSmtp(p => ({ ...p, smtp_password: e.target.value }))}
                    placeholder="16-character App Password" className="pr-9" data-testid="smtp-password" />
                  <button onClick={() => setShowPassword(p => !p)}
                    className="absolute right-2 top-2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 font-ibm mb-1 block">From Name</label>
                <Input value={smtp.from_name || ''} onChange={e => setSmtp(p => ({ ...p, from_name: e.target.value }))}
                  placeholder="Stavan Logistics" data-testid="smtp-from-name" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 font-ibm mb-1 block">From Email</label>
                <Input value={smtp.from_email || ''} onChange={e => setSmtp(p => ({ ...p, from_email: e.target.value }))}
                  placeholder="info@stavan.co.in" data-testid="smtp-from-email" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 font-ibm mb-1 block">Reply-To Email <span className="font-normal">(optional)</span></label>
              <Input value={smtp.reply_to_email || ''} onChange={e => setSmtp(p => ({ ...p, reply_to_email: e.target.value }))}
                placeholder="noreply@stavan.co.in" data-testid="smtp-reply-to" />
            </div>

            {/* Test connection row */}
            <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
              <Input value={testEmail} onChange={e => setTestEmail(e.target.value)}
                placeholder="Send test to (email)..." className="flex-1 text-sm" data-testid="test-email-input" />
              <Button variant="outline" size="sm" onClick={testConnection} disabled={testLoading} data-testid="test-connection-btn">
                {testLoading
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                  : <TestTube2 className="w-3.5 h-3.5 mr-1" />}
                Test Connection
              </Button>
              {smtpStatus === 'ok' && <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />}
              {smtpStatus === 'fail' && <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />}
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={saveSmtp} disabled={smtpSaving} data-testid="save-smtp-btn">
                {smtpSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                Save
              </Button>
            </div>

            {/* Gmail instructions */}
            <div className="bg-blue-50 rounded-xl p-4 text-xs font-ibm text-blue-700 space-y-1">
              <p className="font-semibold">Gmail App Password Setup:</p>
              <p>1. Enable 2-Step Verification on your Google Account</p>
              <p>2. Go to <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline font-semibold">myaccount.google.com/apppasswords</a></p>
              <p>3. Create an App Password: App = "Mail", Device = "Other" → name it "LogisticsHub"</p>
              <p>4. Copy the 16-character password and paste it in the App Password field above</p>
            </div>
          </div>
        )}
      </Card>

      {/* ── Email Templates ── */}
      <Card className="p-6 rounded-xl border border-gray-200 bg-white">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-semibold text-gray-800 font-manrope">Email Templates</h3>
            <p className="text-xs text-gray-400 font-ibm mt-0.5">
              Use <code className="bg-gray-100 px-1 rounded">{'{{variable_name}}'}</code> for dynamic content
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={seedTemplates} disabled={seeding} data-testid="seed-templates-btn">
              {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
              Seed Defaults
            </Button>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setEditTemplate({})} data-testid="new-template-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> New Template
            </Button>
          </div>
        </div>

        {tmplLoading ? (
          <div className="flex items-center gap-2 text-gray-400 py-8 justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-blue-500" /><span className="text-sm">Loading templates...</span>
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <Mail className="w-10 h-10 mx-auto mb-2 text-gray-200" />
            <p className="text-sm font-ibm">No templates yet.</p>
            <button onClick={seedTemplates} className="text-blue-600 text-xs hover:underline mt-1">Seed default templates →</button>
          </div>
        ) : (
          <div className="space-y-2" data-testid="templates-list">
            {templates.map(tmpl => (
              <div key={tmpl.id} className="flex items-start justify-between gap-4 p-4 rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-semibold text-gray-800 font-manrope">{tmpl.name}</span>
                    <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-mono">{tmpl.template_key}</span>
                    {tmpl.is_active === false && <span className="text-[10px] bg-red-50 text-red-500 px-2 py-0.5 rounded-full">Inactive</span>}
                  </div>
                  <p className="text-xs text-gray-500 font-ibm truncate">{tmpl.subject}</p>
                  {tmpl.variables && (
                    <p className="text-[10px] text-gray-400 font-mono mt-1">
                      Variables: {(typeof tmpl.variables === 'string'
                        ? JSON.parse(tmpl.variables || '[]')
                        : tmpl.variables
                      ).join(', ')}
                    </p>
                  )}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => setEditTemplate(tmpl)}
                    className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50" data-testid={`edit-tmpl-${tmpl.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => deleteTemplate(tmpl.id)}
                    className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-tmpl-${tmpl.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {editTemplate !== null && (
        <TemplateModal
          template={Object.keys(editTemplate).length > 0 ? editTemplate : null}
          onClose={() => setEditTemplate(null)}
          onSave={fetchTemplates}
        />
      )}
    </div>
  );
};

export default EmailSettings;
